import React, { useEffect, useState } from "react";
import {
   endpointRequest,
   updateServerSession,
} from "../../../../utilities/requests";
import * as arrow from "apache-arrow";
import ECLFormFieldInput from "./ECLFormFieldInput";
import { translate } from "../../../../utilities/Translate";
import debounce from "../../../../utilities/debounce";
import ECLLoader from "../../../Widgets/ECLLoader/ECLLoader";
export default function ECLFormField(props) {
   const [columns, setColumns] = useState([]);
   const [rows, setRows] = useState([]);
   const [inputType, setInputType] = useState();
   const [error, setError] = useState(null);
   const [loading, setLoading] = useState(true);
   function handleVectorType(colType, updatedRow) {
      switch (colType) {
         case "Utf8": {
            const utf8Array = updatedRow.map((value) =>
               value === null || value === undefined ? null : value,
            );
            return arrow.vectorFromArray(utf8Array, new arrow.Utf8());
         }
         case "Int32": {
            const int32Array = updatedRow.map((value) => {
               if (value === null || value === undefined) {
                  return null; // Handle null or undefined as-is
               }
               if (typeof value === "number") {
                  return Math.trunc(value); // Remove the decimal part if it's a float
               }
               if (!Number.isInteger(Number(value))) {
                  return; // Invalid value handling
               }
               return Number(value); // Return as an integer
            });
            return arrow.vectorFromArray(int32Array, new arrow.Int32());
         }
         case "Int64": {
            const int64Array = updatedRow.map((value) => {
               if (value === null || value === undefined) {
                  return null; // Handle null or undefined as-is
               }
               if (typeof value === "number") {
                  return BigInt(Math.trunc(value)); // Truncate floats to integers and convert to BigInt
               }
               const numericValue = Number(value); // Convert to a number if it's a string
               if (isNaN(numericValue)) {
                  return null; // Handle invalid non-numeric values
               }
               return BigInt(Math.trunc(numericValue)); // Truncate and convert to BigInt
            });
            return arrow.vectorFromArray(int64Array, new arrow.Int64());
         }
         case "Float64": {
            const float64Array = updatedRow.map((value) => {
               if (value === null || value === undefined) {
                  return null; // Handle null or undefined as-is
               }
               if (isNaN(Number(value))) {
                  return null; // Handle invalid non-numeric values
               }
               return value;
            });
            return arrow.vectorFromArray(float64Array, new arrow.Float64());
         }
         case "Timestamp<MILLISECOND>": {
            const timestampArray = updatedRow.map((value) => {
               if (value === null || value === undefined) {
                  return null; // Handle null or undefined as-is
               }
               // Convert value to a Unix timestamp in milliseconds
               const dateObj = new Date(value); // Convert to a Date object
               if (isNaN(dateObj.getTime())) {
                  return null; // Handle invalid dates
               }
               return dateObj.getTime(); // Return time in milliseconds
            });
            return arrow.vectorFromArray(
               timestampArray,
               new arrow.TimestampMillisecond(),
            );
         }
         default: {
            const defaultArray = updatedRow.map((value) =>
               value === null || value === undefined ? null : value,
            );
            return arrow.vectorFromArray(defaultArray);
         }
      }
   }
   // Update row value
   async function handleInput(e) {
      try {
         // Update row value
         const updatedRow = [...rows];
         updatedRow[0] = e.target.value;
         // Prepare column data arrays, handling `null` values
         const colType = columns[0].type.toString();
         const vector = handleVectorType(colType, updatedRow);
         const vectorTable = {
            [columns[0].name]: vector,
         };
         const updatedArrowTable = arrow.tableFromArrays(vectorTable);
         // Set value of first item in the vector
         // Serialize the table to Arrow IPC format
         const writer =
            arrow.RecordBatchStreamWriter.writeAll(updatedArrowTable);
         const buffer = await writer.toUint8Array();
         writer.close();
         // Convert buffer to base64 string
         const base64Encoded = btoa(String.fromCharCode(...buffer));
         const key_vals = [
            {
               key: "input_parameter_edit_data",
               value: {
                  arrowIPCData: base64Encoded,
               },
            },
         ];
         const debouncedUpdateServerSession = debounce(
            updateServerSession,
            400,
         );
         debouncedUpdateServerSession(props, key_vals);
      } catch (error) {
         console.error("Error creating Arrow IPC data:", error);
         setError(error);
      }
   }
   // Send request and initialize table data
   useEffect(() => {
      const rootID = props.headlist.at(-1); //Last item on the headlist is your root
      const sessionID = props.flatDoc[rootID].session;
      endpointRequest(props.parentObject, props.block.id, sessionID, "AllData")
         .then((res) => {
            setRows(res.rows[0]);
            setColumns(res.columns);
            switch (res.columns[0].typeId) {
               case arrow.Type.Binary:
               case arrow.Type.FixedSizeBinary:
                  setInputType("text");
                  break;
               case arrow.Type.Bool:
               case arrow.Type.Utf8:
                  setInputType("text");
                  break;
               case arrow.Type.Int:
               case arrow.Type.Int8:
               case arrow.Type.Int16:
               case arrow.Type.Int32:
               case arrow.Type.Int64:
                  setInputType("number");
                  break;
               case arrow.Type.Decimal:
               case arrow.Type.Float:
               case arrow.Type.Float16:
               case arrow.Type.Float32:
               case arrow.Type.Float64:
                  setInputType("number");
                  break;
               case arrow.Type.Date:
               case arrow.Type.DateMillisecond:
               case arrow.Type.Timestamp:
                  setInputType("datetime");
                  break;
               default:
                  break;
            }
            setLoading(false);
         })
         .catch((error) => {
            console.error(error);
            setError(translate("Error loading form field"));
         });
   }, [props.block.react.refresh]);
   if (!rows || loading || error) {
      return <ECLLoader message={error} />;
   }
   return (
      <div>
         <ECLFormFieldInput
            {...props}
            handleInput={handleInput}
            inputType={inputType}
            rows={rows}
            columns={columns}
         />
      </div>
   );
}
